import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Avatar,
  Badge,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { appConfig } from '../../config/app';
import NotificationPopover from './NotificationPopover';
import UserMenu from './UserMenu';

// Kept in sync with the notifications array in NotificationPopover
const NOTIFICATION_COUNT = 4;

interface TopBarProps {
  drawerOpen: boolean;
  onDrawerToggle: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ drawerOpen, onDrawerToggle }) => {
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const { logout, userName } = useAuth();
  const theme = useTheme();
  const avatarLetter = userName ? userName.charAt(0).toUpperCase() : 'U';

  const handleLogout = () => {
    setUserMenuAnchor(null);
    logout();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="toggle drawer"
          onClick={onDrawerToggle}
          edge="start"
          sx={{ marginRight: 2 }}
        >
          {drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={appConfig.logoPath}
            alt="App icon"
            onClick={() => window.location.reload()}
            sx={{ width: 200, height: 50, display: 'block', cursor: 'pointer' }}
          />
          {(appConfig.env === 'dev' || appConfig.env === 'development') && (
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, color: 'red' }}>
              - DEVELOPMENT ENVIRONMENT
            </Typography>
          )}
          {appConfig.env === 'stage' && (
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, color: 'orange' }}>
              - STAGE ENVIRONMENT
            </Typography>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Notifications">
            <IconButton
              onClick={(e) => setNotificationAnchor(e.currentTarget)}
              size="small"
              color="inherit"
            >
              <Badge badgeContent={NOTIFICATION_COUNT} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title={userName ? `${userName} (Account)` : 'Account'}>
            <IconButton
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              size="small"
              aria-controls={Boolean(userMenuAnchor) ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(userMenuAnchor) ? 'true' : undefined}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>{avatarLetter}</Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        <NotificationPopover
          anchorEl={notificationAnchor}
          onClose={() => setNotificationAnchor(null)}
        />

        <UserMenu
          anchorEl={userMenuAnchor}
          userName={userName}
          onClose={() => setUserMenuAnchor(null)}
          onLogout={handleLogout}
        />
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
